import { useEffect, useMemo, useState } from "react";

import * as api from "../api/status";
import { ApiException } from "../api/client";
import { IncidentList } from "../components/IncidentList";
import { ResponseTimeChart } from "../components/ResponseTimeChart";
import { ServiceGroupCard } from "../components/ServiceGroupCard";
import { StatusTabs } from "../components/StatusTabs";
import { StatusTopBar } from "../components/StatusTopBar";

const BANNER: Record<
  api.ServiceStatus,
  { text: string; bg: string; ring: string }
> = {
  operational: {
    text: "All Systems Operational",
    bg: "bg-status-ok",
    ring: "ring-status-ok/30",
  },
  degraded: {
    text: "Some services are degraded",
    bg: "bg-status-degraded",
    ring: "ring-status-degraded/30",
  },
  partial_outage: {
    text: "Partial service outage",
    bg: "bg-status-partial",
    ring: "ring-status-partial/30",
  },
  major_outage: {
    text: "Major outage in progress",
    bg: "bg-status-major",
    ring: "ring-status-major/30",
  },
  unknown: {
    text: "Service status unavailable",
    bg: "bg-status-unknown",
    ring: "ring-status-unknown/30",
  },
};

const UPTIME_DAYS = 90;

export function Home(): JSX.Element {
  const [overview, setOverview] = useState<api.OverviewResponse | null>(null);
  const [uptime, setUptime] = useState<api.UptimeResponse | null>(null);
  const [metrics, setMetrics] = useState<api.MetricsResponse | null>(null);
  const [domainHealth, setDomainHealth] = useState<api.DomainHealthResponse | null>(null);
  const [range, setRange] = useState<api.MetricsRange>("24h");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        
        const [o, u, d] = await Promise.all([
          api.getOverview(),
          api.getUptime(UPTIME_DAYS),
          api.getDomainHealth(),
        ]);
        if (cancelled) return;
        
        try {
          localStorage.setItem("dr_last_overview", JSON.stringify(o));
          localStorage.setItem("dr_last_uptime", JSON.stringify(u));
          localStorage.setItem("dr_last_domain", JSON.stringify(d));
        } catch(e){}
        
        
        // Filter out deprecated marketplace services
        if (o) {
          o.services = o.services.filter(s => !s.key.includes("mp_") && !s.key.includes("marketplace"));
          o.activeIncidents = o.activeIncidents.filter(i => !i.service.includes("mp_") && !i.service.includes("marketplace"));
        }
        if (u) {
          u.services = u.services.filter(s => !s.key.includes("mp_") && !s.key.includes("marketplace"));
        }
        
        setOverview(o);
        setUptime(u);
        setDomainHealth(d);

        setError(null); // Clear error if recovered
      } catch (err: any) {
        if (!cancelled) {
          const isOffline = err.status === 503 || err.status === 502 || err.status === 504 || (err.apiError && err.apiError.code === "network_error");
          
          if (isOffline) {
            try {
              const cachedO = JSON.parse(localStorage.getItem("dr_last_overview") || "null");
              if (cachedO) {
                cachedO.status = "major_outage";
                cachedO.services.forEach((s: any) => s.status = "major_outage");
                setOverview(cachedO);
                
                const cachedU = JSON.parse(localStorage.getItem("dr_last_uptime") || "null");
                if (cachedU) setUptime(cachedU);
                else setUptime({ services: [], globalPct: 0, daysComputed: 0 } as any);
                
                const cachedD = JSON.parse(localStorage.getItem("dr_last_domain") || "null");
                if (cachedD) {
                   cachedD.domains.forEach((dom: any) => {
                     dom.status = "down";
                     dom.subdomains.forEach((sub: any) => sub.status = "down");
                   });
                   setDomainHealth(cachedD);
                }
                return;
              }
            } catch (e) {}
            
            // Fallback if no cache
            setOverview({
              status: "major_outage",
              services: [],
              uptime60d: { percentage: 0, daysComputed: 0 },
              activeIncidents: [],
              recentlyResolvedIncidents: [],
              generatedAt: new Date().toISOString()
            } as any);
            setUptime({ services: [], globalPct: 0, daysComputed: 0 } as any);
            setDomainHealth({ domains: [], generatedAt: new Date().toISOString() } as any);
            return;
          }

          setError(
            err instanceof ApiException ? err.apiError.message : "Failed to load",
          );
        }
      }

    }
    void load();
    const t = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api.getMetrics(range).then((m) => {
      if (!cancelled) setMetrics(m);
    });
    return () => {
      cancelled = true;
    };
  }, [range]);

  if (error) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="card">
          <h1 className="text-xl font-semibold">Status page unavailable</h1>
          <p className="mt-2 text-sm text-text-muted">{error}</p>
        </div>
      </main>
    );
  }

  if (!overview || !uptime) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-sm text-text-muted">Loading…</p>
      </main>
    );
  }

  const banner = BANNER[overview.status];
  const uptimeByKey = new Map(uptime.services.map((s) => [s.key, s]));
  // 90-day average across non-external services — externals have no
  // probes so they would pull the global to 0 if included.
  const globalUptime90 = (() => {
    let total = 0;
    let ok = 0;
    for (const s of uptime.services) {
      if (s.external) continue;
      for (const d of s.days) {
        total += d.totalProbes;
        ok += d.okProbes;
      }
    }
    return total === 0 ? 100 : (ok / total) * 100;
  })();
  // Group services by their `group` field, preserving first-seen order
  // (which matches the registry order from the backend).
  const groupedServices: [string, typeof overview.services][] = (() => {
    const m = new Map<string, typeof overview.services>();
    for (const s of overview.services) {
      const arr = m.get(s.group) ?? [];
      arr.push(s);
      m.set(s.group, arr);
    }
    return [...m.entries()];
  })();

  return (
    <main className="mx-auto max-w-4xl px-6 py-10">
      <StatusTopBar />
      <StatusTabs />

      {/* 1. Global status banner — point focal #1. */}
      <section
        className={`mb-10 rounded-2xl px-7 py-7 text-white shadow-lg ring-1 ${banner.bg} ${banner.ring}`}
        role="status"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-3 w-3 animate-pulse rounded-full bg-white/90 shadow"
              aria-hidden
            />
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {banner.text}
            </h1>
          </div>
          <p className="text-xs text-white/80">
            Refreshed {new Date(overview.generatedAt).toLocaleTimeString()}
          </p>
        </div>
      </section>

      {/* 2. Active incidents — only rendered when something is actually wrong. */}
      {overview.activeIncidents.length > 0 && (
        <section className="mb-8 card space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-text-secondary">
            Active incidents
          </h2>
          <IncidentList items={overview.activeIncidents} />
        </section>
      )}

      {/* 3. Services — grouped by category, with sub-grouping inside. */}
      <section className="mb-10">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wide text-text-secondary">
            Services
          </h2>
          <span className="text-xs text-text-muted">
            Uptime over the last {UPTIME_DAYS} days
          </span>
        </div>
        <div className="space-y-5">
          {groupedServices.map(([group, items]) => (
            <ServiceGroupCard
              key={group}
              group={group}
              services={items}
              uptimeByKey={uptimeByKey}
              spanDays={UPTIME_DAYS}
            />
          ))}
        </div>
        <p className="mt-4 text-right text-xs text-text-muted">
          {globalUptime90.toFixed(3)}% uptime across monitored services
        </p>
      </section>

      {/* 4. Recently resolved — collapsed when there are none. */}
      {overview.recentlyResolvedIncidents.length > 0 && (
        <section className="mb-8 card space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-text-secondary">
            Recently resolved
          </h2>
          <IncidentList items={overview.recentlyResolvedIncidents} />
        </section>
      )}

      {/* 5. DOMAIN HEALTH — mirror subdomain status per root domain. */}
      {domainHealth && domainHealth.domains.length > 0 && (
        <section className="mb-10">
          <div className="mb-4">
            <h2 className="text-sm font-medium uppercase tracking-wide text-text-secondary">
              Domains
            </h2>
          </div>
          <div className="space-y-4">
            {domainHealth.domains.map((d) => {
              const isOnion = d.domain.endsWith(".onion");
              return (
                <div key={d.domain} className="card">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isOnion && (
                        <span className="shrink-0 rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-purple-400 border border-purple-500/20">
                          Onion
                        </span>
                      )}
                      <span
                        className="font-mono text-sm font-semibold text-text-primary truncate"
                        title={d.domain}
                      >
                        {isOnion
                          ? `${d.domain.slice(0, 8)}...${d.domain.slice(-6)}`
                          : d.domain}
                      </span>
                    </div>
                    <DomainStatusBadge status={d.status} />
                  </div>
                  {isOnion && (
                    <p className="mb-3 font-mono text-[10px] text-text-muted break-all select-all">
                      {d.domain}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                    {d.subdomains.map((sub) => (
                      <div
                        key={sub.subdomain}
                        className="flex flex-col gap-1 rounded border border-border bg-bg-alt px-3 py-2"
                      >
                        <span className="font-mono text-xs text-text-muted">
                          {isOnion ? sub.subdomain : `${sub.subdomain}.`}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <DomainStatusDot status={sub.status} />
                          <span className="text-xs text-text-secondary capitalize">
                            {sub.status}
                          </span>
                        </div>
                        {sub.latencyMs !== null && (
                          <span className="text-xs tabular-nums text-text-muted">
                            {sub.latencyMs} ms
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 6. SYSTEM METRICS — premium SaaS card: header + KPI + chart + micro-KPIs. */}
      <section className="mb-10">
        <SystemMetricsCard metrics={metrics} range={range} setRange={setRange} />
      </section>

      <footer className="flex flex-wrap items-center justify-end gap-3 text-xs text-text-muted">
        <button
          onClick={() => window.print()}
          className="rounded border border-border px-2 py-1 hover:text-text-secondary no-print"
        >
          Print / Export PDF
        </button>
      </footer>
    </main>
  );
}

const RANGE_LABEL: Record<api.MetricsRange, string> = {
  "24h": "Day",
  "7d": "Week",
  "30d": "Month",
};

const RANGE_COMPARE_LABEL: Record<api.MetricsRange, string> = {
  "24h": "vs previous 12 h",
  "7d": "vs previous 3.5 d",
  "30d": "vs previous 15 d",
};

interface MetricsCardProps {
  metrics: api.MetricsResponse | null;
  range: api.MetricsRange;
  setRange: (r: api.MetricsRange) => void;
}

interface AggregatedMetrics {
  /** Latest non-null bucket — the headline KPI. */
  latestAvg: number | null;
  /** Mean of bucket avgs over the second half of the window. */
  recentAvg: number | null;
  /** Mean of bucket avgs over the first half — used for delta. */
  priorAvg: number | null;
  /** Mean of bucket p95s over the second half. */
  recentP95: number | null;
  /** 1 - mean okRatio over the second half, as a percentage. */
  recentErrorPct: number | null;
  /** Number of buckets in the window. */
  buckets: number;
}

function aggregate(buckets: api.MetricsBucket[]): AggregatedMetrics {
  if (buckets.length === 0) {
    return {
      latestAvg: null,
      recentAvg: null,
      priorAvg: null,
      recentP95: null,
      recentErrorPct: null,
      buckets: 0,
    };
  }
  // Latest non-null avg, walking backwards.
  let latestAvg: number | null = null;
  for (let i = buckets.length - 1; i >= 0; i--) {
    if (buckets[i]!.avgLatencyMs !== null) {
      latestAvg = buckets[i]!.avgLatencyMs;
      break;
    }
  }
  const mid = Math.floor(buckets.length / 2);
  const first = buckets.slice(0, mid);
  const second = buckets.slice(mid);
  const mean = (xs: (number | null)[]): number | null => {
    const ok = xs.filter((v): v is number => v !== null);
    return ok.length === 0 ? null : ok.reduce((a, b) => a + b, 0) / ok.length;
  };
  const recentAvg = mean(second.map((b) => b.avgLatencyMs));
  const priorAvg = mean(first.map((b) => b.avgLatencyMs));
  const recentP95 = mean(second.map((b) => b.p95LatencyMs));
  const recentErrorPct =
    second.length === 0
      ? null
      : (1 - second.reduce((a, b) => a + b.okRatio, 0) / second.length) * 100;
  return {
    latestAvg,
    recentAvg,
    priorAvg,
    recentP95,
    recentErrorPct,
    buckets: buckets.length,
  };
}

function SystemMetricsCard({
  metrics,
  range,
  setRange,
}: MetricsCardProps): JSX.Element {
  const agg = useMemo(() => aggregate(metrics?.buckets ?? []), [metrics]);
  const delta =
    agg.recentAvg !== null && agg.priorAvg !== null && agg.priorAvg > 0
      ? ((agg.recentAvg - agg.priorAvg) / agg.priorAvg) * 100
      : null;
  const deltaTone =
    delta === null
      ? "text-text-muted"
      : delta > 5
        ? "text-status-degraded"
        : delta < -5
          ? "text-status-ok"
          : "text-text-muted";
  const deltaSign = delta === null ? "" : delta > 0 ? "+" : "";

  return (
    <article className="rounded-2xl border border-white/[0.07] bg-dr-surface/60 p-7 shadow-sm backdrop-blur-sm">
      {/* Header: section eyebrow + segmented control. */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-muted">
          System metrics
        </p>
        <div
          className="inline-flex rounded-lg border border-white/10 bg-dr-bg/60 p-0.5 text-xs no-print"
          role="tablist"
        >
          {(["24h", "7d", "30d"] as api.MetricsRange[]).map((r) => (
            <button
              key={r}
              role="tab"
              aria-selected={r === range}
              onClick={() => setRange(r)}
              className={`rounded-md px-3 py-1.5 transition-colors ${
                r === range
                  ? "bg-dr-blue text-white shadow"
                  : "text-text-secondary hover:text-dr-txt"
              }`}
            >
              {RANGE_LABEL[r]}
            </button>
          ))}
        </div>
      </header>

      {/* KPI: title left, value right. */}
      <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-dr-heading">
            API Response Time
          </h2>
          <p className="mt-0.5 text-xs text-text-muted">
            Average across all monitored services
          </p>
        </div>
        <div className="text-right">
          <p className="text-4xl font-semibold tabular-nums text-dr-heading">
            {agg.latestAvg !== null ? Math.round(agg.latestAvg) : "—"}
            <span className="ml-1 text-base font-normal text-text-muted">ms</span>
          </p>
          {delta !== null && (
            <p className={`mt-1 text-xs ${deltaTone}`}>
              {deltaSign}
              {delta.toFixed(1)}% {RANGE_COMPARE_LABEL[range]}
            </p>
          )}
        </div>
      </div>

      {/* Chart. */}
      <div className="mt-6">
        {metrics ? (
          <ResponseTimeChart buckets={metrics.buckets} />
        ) : (
          <div className="flex h-[240px] items-center justify-center text-sm text-text-muted">
            Loading…
          </div>
        )}
      </div>

      {/* Micro-KPIs. */}
      <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-white/[0.06] pt-5 sm:grid-cols-4">
        <MicroKpi
          label="Avg latency"
          value={agg.recentAvg !== null ? `${Math.round(agg.recentAvg)} ms` : "—"}
        />
        <MicroKpi
          label="p95 latency"
          value={agg.recentP95 !== null ? `${Math.round(agg.recentP95)} ms` : "—"}
        />
        <MicroKpi
          label="Error rate"
          value={
            agg.recentErrorPct !== null
              ? `${agg.recentErrorPct.toFixed(2)}%`
              : "—"
          }
          tone={
            agg.recentErrorPct !== null && agg.recentErrorPct > 1
              ? "warn"
              : "default"
          }
        />
        <MicroKpi
          label="Data points"
          value={agg.buckets.toString()}
        />
      </dl>
    </article>
  );
}

function MicroKpi({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "warn";
}): JSX.Element {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
        {label}
      </dt>
      <dd
        className={`mt-1 text-lg font-semibold tabular-nums ${
          tone === "warn" ? "text-status-degraded" : "text-dr-heading"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}


// ── Domain health helpers ──────────────────────────────────────────────────

const DOMAIN_STATUS_COLORS: Record<api.DomainProbeStatus, string> = {
  operational: "bg-status-ok",
  degraded:    "bg-status-degraded",
  down:        "bg-status-major",
  unknown:     "bg-status-unknown",
};

const DOMAIN_STATUS_BADGE: Record<api.DomainProbeStatus, string> = {
  operational: "text-status-ok border-status-ok/30 bg-status-ok/10",
  degraded:    "text-status-degraded border-status-degraded/30 bg-status-degraded/10",
  down:        "text-status-major border-status-major/30 bg-status-major/10",
  unknown:     "text-text-muted border-border bg-bg-alt",
};

function DomainStatusDot({ status }: { status: api.DomainProbeStatus }) {
  return (
    <span
      className={`inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full ${DOMAIN_STATUS_COLORS[status]}`}
    />
  );
}

function DomainStatusBadge({ status }: { status: api.DomainProbeStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium capitalize ${DOMAIN_STATUS_BADGE[status]}`}
    >
      <DomainStatusDot status={status} />
      {status.replace("_", " ")}
    </span>
  );
}
